import { useMemo } from 'react'

import { getCitationNodesFromEditorState } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/CitationNode/CitationNode'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { useLazyGetEffectiveNodeQuery, useLazyGetInputParametersHierarchyDefaultQuery } from '../../../store/ai/inputParametersHierarchy'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { selectCurrentUserEmailIntegration } from '../../../store/emailHub/emailUsers'
import { useExecuteAiAgentMutation } from '../../../store/ai/aiAgent'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  clearNewEmailResult,
  selectNewEmailCitations,
  selectNewEmailRecipientContact,
  selectNewEmailRelatedContact,
  selectNewEmailResult,
  setNewEmailAiAgentId,
  setNewEmailExecutionLoading,
  setNewEmailResult,
} from '../../../store/emailHub/newEmail'

import { InputParameterHierarchyType } from '../../../components/InputParameterHierarchy/model'

import { NewEmailResult } from '../../../models/NewEmail'
import { AIAgentType } from '../../../models/AIAgent'
import { EmailType } from '../../../models/EmailType'

import { Citation } from '../../AIAgent/AIAgentResultCitations/AIAgentResultCitations'
import { useAIAgentResultState } from '../../AIAgent/hooks/useAIAgentResultState'

import { hasClientRole } from './utils'
import { addMessage, clearChat } from '../../../store/streamingChat/streamingChat'

export const useNewEmailResult = () => {
  const newEmailResult = useAppSelector(selectNewEmailResult)
  const citations = useAppSelector(selectNewEmailCitations)

  const newEmailState = useAIAgentResultState(newEmailResult?.email)

  const filteredCitations = useMemo(() => {
    if (!citations?.length) {
      return []
    }
    const citationNodes = getCitationNodesFromEditorState(newEmailState.editorState)
    const nodeExistsForCitation = (citation: Citation) => citationNodes.find((e) => e.getCitationId() === citation.id)
    return citations.filter(nodeExistsForCitation)
  }, [citations, newEmailState])

  return { newEmailState, filteredCitations }
}

export const useExecuteNewEmail = () => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const emailIntegration = useAppSelector(selectCurrentUserEmailIntegration)
  const currentUserId = useAppSelector(selectSafeAuthenticatedUser).id

  const recipientContact = useAppSelector(selectNewEmailRecipientContact)
  const relatedContact = useAppSelector(selectNewEmailRelatedContact)

  const [triggerExecuteAiAgent] = useExecuteAiAgentMutation()
  const [getInputParameterHierarchyNode] = useLazyGetEffectiveNodeQuery()
  const [getInputParameterHierarchyDefault] = useLazyGetInputParametersHierarchyDefaultQuery()

  const fetchEmailTemplate = async (emailType: EmailType | 'custom'): Promise<string> => {
    if (emailType === 'custom') {
      return ''
    }

    const template = await getInputParameterHierarchyNode(
      {
        accountId,
        type: InputParameterHierarchyType.NEW_EMAIL_TEMPLATE,
        typeId: 'email-template',
        context: { effective: true, userId: currentUserId, emailTypeId: emailType.id, contextType: 'email_type' },
      },
      true,
    ).unwrap()

    if (template) {
      return template.inputParameters['template'] ?? ''
    }

    const defaultTemplate = await getInputParameterHierarchyDefault(
      {
        accountId,
        type: InputParameterHierarchyType.NEW_EMAIL_TEMPLATE,
        typeId: emailType.systemEmailTypeId ?? 'default-email',
      },
      true,
    ).unwrap()

    if (defaultTemplate) {
      return defaultTemplate.inputParameters['template'] ?? ''
    }

    return ''
  }

  const _handleNewEmailExecution = async (emailType: EmailType | 'custom', emailDescription?: string) => {
    if (!emailIntegration || !recipientContact) {
      return
    }
    const { serviceId: emailConnector } = emailIntegration

    dispatch(clearNewEmailResult())
    dispatch(clearChat())
    dispatch(setNewEmailExecutionLoading(true))
    try {
      const template = await fetchEmailTemplate(emailType)
      const { data: aiAgentResult } = await triggerExecuteAiAgent({
        accountId,
        payload: {
          type: AIAgentType.NEW_EMAIL,
          context: {
            recipientName: recipientContact.name,
            recipientRole: hasClientRole(recipientContact) ? 'Client' : 'Team Member',
            contactId: relatedContact ? relatedContact.id : recipientContact.id,
            emailTemplate: template,
            assistantInstructions: emailType === 'custom' ? '' : emailType.detectionConfig.aiAssistantInstructions,
            emailDescription,
            emailConnector,
          },
        },
      })
      if (!aiAgentResult) {
        return
      }
      const { result, aiAgentId } = aiAgentResult
      dispatch(setNewEmailAiAgentId(aiAgentId))
      dispatch(setNewEmailResult(result as NewEmailResult))
      const { message } = result
      if (message) {
        dispatch(addMessage({ type: 'agent', message }))
      }
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setNewEmailExecutionLoading(false))
    }
  }

  const handleNewEmailExecution = tracked(
    _handleNewEmailExecution,
    { eventName: TrackEvent.NEW_EMAIL_EXECUTED },
    {
      eventDetailsExtractor: (emailType: EmailType | 'custom', emailDescription?: string) => ({
        emailDescription,
      }),
    },
  )

  return { handleNewEmailExecution }
}
